import type {
  CompanyUsageByModelDTO,
  CompanyUsageMemberDetailDTO,
  CompanyUsageSummaryDTO,
  CompanyUsageTimeseriesDTO,
  CompanyUsageTopConversationsDTO,
  PaginatedConversationUsageDTO,
  PaginatedCompanyUsageEventsDTO,
  PaginatedCompanyUsageMembersDTO,
  UsagePricingModelDTO,
  UsagePricingModelUpsertDTO,
  UsagePricingModelStatus,
  UsagePricingModelsDTO,
  UsageSummaryDTO,
} from '@shared/schemas/usage';
import { apiBaseUrl } from '@/lib/auth-client';
import { ApiError } from '@/lib/api-error';

type ApiSuccessResponse<T> = {
  data: T;
  success: true;
};

function createApiUrl(path: string, query?: Record<string, string | number | boolean | null | undefined>) {
  const url = new URL(path, apiBaseUrl);

  if (!query) {
    return url;
  }

  Object.entries(query).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') {
      return;
    }

    url.searchParams.set(key, String(value));
  });

  return url;
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const payload = await response.json().catch(() => null) as ({
    error?: {
      code?: string;
      details?: Record<string, unknown>;
      message?: string;
    };
  } & T) | null;

  if (!response.ok || payload === null) {
    const fallbackMessage = response.statusText || 'La requête a échoué.';
    throw new ApiError({
      code: payload?.error?.code,
      details: payload?.error?.details,
      message: payload?.error?.message ?? fallbackMessage,
      status: response.status,
    });
  }

  return payload as T;
}

async function getUsage<T>(path: string, query?: Record<string, string | number | boolean | null | undefined>) {
  const response = await fetch(createApiUrl(path, query), {
    credentials: 'include',
    method: 'GET',
  });

  const payload = await parseJsonResponse<ApiSuccessResponse<T>>(response);
  return payload.data;
}

async function getCompanyUsage<T>(
  path: string,
  query?: Record<string, string | number | boolean | null | undefined>,
) {
  const response = await fetch(createApiUrl(path, query), {
    credentials: 'include',
    method: 'GET',
  });

  return parseJsonResponse<T>(response);
}

async function sendUsage<T>(
  method: 'PATCH' | 'PUT',
  path: string,
  body: Record<string, unknown>,
) {
  const response = await fetch(createApiUrl(path), {
    body: JSON.stringify(body),
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
    },
    method,
  });

  const payload = await parseJsonResponse<ApiSuccessResponse<T>>(response);
  return payload.data;
}

export function getMyUsage(days = 30) {
  return getUsage<UsageSummaryDTO>('/api/usage/me', { days });
}

export function getMyConversationsUsage(days = 30, page = 1, pageSize = 10) {
  return getUsage<PaginatedConversationUsageDTO>('/api/usage/me/conversations', {
    days,
    page,
    pageSize,
  });
}

export function getAdminPricingModels() {
  return getUsage<UsagePricingModelsDTO>('/api/usage/admin/pricing/models');
}

export function getCompanyUsageSummary(days = 30) {
  return getCompanyUsage<CompanyUsageSummaryDTO>('/api/usage/company/summary', { days });
}

export function getCompanyUsageTimeseries(days = 30) {
  return getCompanyUsage<CompanyUsageTimeseriesDTO>('/api/usage/company/timeseries', { days });
}

export function getCompanyUsageByModel(days = 30) {
  return getCompanyUsage<CompanyUsageByModelDTO>('/api/usage/company/by-model', { days });
}

export function getCompanyTopConversations(days = 30, limit = 10) {
  return getCompanyUsage<CompanyUsageTopConversationsDTO>('/api/usage/company/top-conversations', {
    days,
    limit,
  });
}

export function getCompanyUsageMembers(days = 30, page = 1, pageSize = 20) {
  return getCompanyUsage<PaginatedCompanyUsageMembersDTO>('/api/usage/company/members', {
    days,
    page,
    pageSize,
  });
}

export function getCompanyUsageMember(userId: string, days = 30) {
  return getCompanyUsage<CompanyUsageMemberDetailDTO>(`/api/usage/company/members/${userId}`, { days });
}

export function getCompanyUsageEvents(days = 30, page = 1, pageSize = 20) {
  return getCompanyUsage<PaginatedCompanyUsageEventsDTO>('/api/usage/company/events', {
    days,
    page,
    pageSize,
  });
}

export function getAdminCompanyUsageSummary(companyId: string, days = 30) {
  return getCompanyUsage<CompanyUsageSummaryDTO>(`/api/admin/companies/${companyId}/usage/summary`, { days });
}

export function getAdminCompanyUsageTimeseries(companyId: string, days = 30) {
  return getCompanyUsage<CompanyUsageTimeseriesDTO>(`/api/admin/companies/${companyId}/usage/timeseries`, { days });
}

export function getAdminCompanyUsageByModel(companyId: string, days = 30) {
  return getCompanyUsage<CompanyUsageByModelDTO>(`/api/admin/companies/${companyId}/usage/by-model`, { days });
}

export function getAdminCompanyTopConversations(companyId: string, days = 30, limit = 10) {
  return getCompanyUsage<CompanyUsageTopConversationsDTO>(
    `/api/admin/companies/${companyId}/usage/top-conversations`,
    { days, limit },
  );
}

export function getAdminCompanyUsageMembers(companyId: string, days = 30, page = 1, pageSize = 20) {
  return getCompanyUsage<PaginatedCompanyUsageMembersDTO>(`/api/admin/companies/${companyId}/usage/members`, {
    days,
    page,
    pageSize,
  });
}

export function upsertAdminPricingModel(input: UsagePricingModelUpsertDTO) {
  return sendUsage<UsagePricingModelDTO>('PUT', '/api/usage/admin/pricing/models', input);
}

export function setAdminPricingModelStatus(pricingId: string, status: UsagePricingModelStatus) {
  return sendUsage<UsagePricingModelDTO>(
    'PATCH',
    `/api/usage/admin/pricing/models/${pricingId}/status`,
    { status },
  );
}
